/**
 * @fileoverview Offline Storage Utilities using IndexedDB
 *
 * Provides:
 * - Local data caching for offline access
 * - Queue for pending operations (sync when online)
 * - Jobs data persistence for technicians
 *
 * @module lib/offline-storage
 */

// ============================================================================
// Types
// ============================================================================

export interface PendingOperation {
  id: string;
  type: 'job_status_update' | 'survey_submit' | 'photo_upload';
  data: Record<string, unknown>;
  createdAt: string;
  retryCount: number;
}

export interface CachedJob {
  id: string;
  data: Record<string, unknown>;
  cachedAt: string;
}

// ============================================================================
// Constants
// ============================================================================

const DB_NAME = 'aurora_offline_db';
const DB_VERSION = 1;

const STORES = {
  PENDING_OPERATIONS: 'pending_operations',
  CACHED_JOBS: 'cached_jobs',
  CACHED_SURVEYS: 'cached_surveys',
} as const;

// ============================================================================
// Database Initialization
// ============================================================================

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Opens or creates the IndexedDB database
 */
function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      if (import.meta.env.DEV) console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create stores if they don't exist
      if (!db.objectStoreNames.contains(STORES.PENDING_OPERATIONS)) {
        const pendingStore = db.createObjectStore(STORES.PENDING_OPERATIONS, { keyPath: 'id' });
        pendingStore.createIndex('type', 'type', { unique: false });
        pendingStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.CACHED_JOBS)) {
        const jobsStore = db.createObjectStore(STORES.CACHED_JOBS, { keyPath: 'id' });
        jobsStore.createIndex('cachedAt', 'cachedAt', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.CACHED_SURVEYS)) {
        db.createObjectStore(STORES.CACHED_SURVEYS, { keyPath: 'id' });
      }
    };
  });

  return dbPromise;
}

// ============================================================================
// Pending Operations Queue
// ============================================================================

/**
 * Add an operation to the pending queue
 */
export async function queueOperation(
  type: PendingOperation['type'],
  data: Record<string, unknown>
): Promise<string> {
  const db = await openDatabase();
  const id = `${type}_${Date.now()}_${Math.random().toString(36).slice(2)}`;

  const operation: PendingOperation = {
    id,
    type,
    data,
    createdAt: new Date().toISOString(),
    retryCount: 0,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.PENDING_OPERATIONS, 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_OPERATIONS);
    const request = store.add(operation);

    request.onsuccess = () => resolve(id);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all pending operations
 */
export async function getPendingOperations(): Promise<PendingOperation[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.PENDING_OPERATIONS, 'readonly');
    const store = transaction.objectStore(STORES.PENDING_OPERATIONS);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get count of pending operations
 */
export async function getPendingOperationsCount(): Promise<number> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.PENDING_OPERATIONS, 'readonly');
    const store = transaction.objectStore(STORES.PENDING_OPERATIONS);
    const request = store.count();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove a completed operation from the queue
 */
export async function removeOperation(id: string): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.PENDING_OPERATIONS, 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_OPERATIONS);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Update retry count for a failed operation
 */
export async function incrementRetryCount(id: string): Promise<void> {
  const db = await openDatabase();

  const transaction = db.transaction(STORES.PENDING_OPERATIONS, 'readwrite');
  const store = transaction.objectStore(STORES.PENDING_OPERATIONS);

  return new Promise((resolve, reject) => {
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const operation = getRequest.result as PendingOperation | undefined;
      if (operation) {
        operation.retryCount++;
        const putRequest = store.put(operation);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve();
      }
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
}

// ============================================================================
// Jobs Cache
// ============================================================================

/**
 * Cache jobs data for offline access
 */
export async function cacheJobs(jobs: Record<string, unknown>[]): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.CACHED_JOBS, 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_JOBS);
    const now = new Date().toISOString();

    // Clear existing and add new
    const clearRequest = store.clear();

    clearRequest.onsuccess = () => {
      jobs.forEach((job) => {
        const cached: CachedJob = {
          id: job.id as string,
          data: job,
          cachedAt: now,
        };
        store.add(cached);
      });
      resolve();
    };

    clearRequest.onerror = () => reject(clearRequest.error);
  });
}

/**
 * Get cached jobs for offline display
 */
export async function getCachedJobs(): Promise<Record<string, unknown>[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.CACHED_JOBS, 'readonly');
    const store = transaction.objectStore(STORES.CACHED_JOBS);
    const request = store.getAll();

    request.onsuccess = () => {
      const cached = request.result as CachedJob[];
      resolve(cached.map(c => c.data));
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get a single cached job by ID
 */
export async function getCachedJob(id: string): Promise<Record<string, unknown> | null> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.CACHED_JOBS, 'readonly');
    const store = transaction.objectStore(STORES.CACHED_JOBS);
    const request = store.get(id);

    request.onsuccess = () => {
      const cached = request.result as CachedJob | undefined;
      resolve(cached?.data || null);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Update a cached job locally (for optimistic updates)
 */
export async function updateCachedJob(
  id: string,
  updates: Record<string, unknown>
): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.CACHED_JOBS, 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_JOBS);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const cached = getRequest.result as CachedJob | undefined;
      if (cached) {
        cached.data = { ...cached.data, ...updates };
        cached.cachedAt = new Date().toISOString();
        const putRequest = store.put(cached);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve();
      }
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
}

// ============================================================================
// Survey Cache
// ============================================================================

interface CachedSurvey {
  id: string;
  jobId: string | null;
  entries: Record<string, unknown>[];
  savedAt: string;
}

/**
 * Save survey data locally (for offline submission)
 */
export async function saveSurveyLocally(
  jobId: string | null,
  entries: Record<string, unknown>[]
): Promise<string> {
  const db = await openDatabase();
  const id = `survey_${Date.now()}`;

  const survey: CachedSurvey = {
    id,
    jobId,
    entries,
    savedAt: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.CACHED_SURVEYS, 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_SURVEYS);
    const request = store.add(survey);

    request.onsuccess = () => resolve(id);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all locally saved surveys
 */
export async function getLocalSurveys(): Promise<CachedSurvey[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.CACHED_SURVEYS, 'readonly');
    const store = transaction.objectStore(STORES.CACHED_SURVEYS);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove a locally saved survey
 */
export async function removeLocalSurvey(id: string): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.CACHED_SURVEYS, 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_SURVEYS);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Clear all offline data (for logout)
 */
export async function clearAllOfflineData(): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(
      [STORES.PENDING_OPERATIONS, STORES.CACHED_JOBS, STORES.CACHED_SURVEYS],
      'readwrite'
    );

    transaction.objectStore(STORES.PENDING_OPERATIONS).clear();
    transaction.objectStore(STORES.CACHED_JOBS).clear();
    transaction.objectStore(STORES.CACHED_SURVEYS).clear();

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

/**
 * Check if IndexedDB is available
 */
export function isIndexedDBAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

// ============================================================================
// Photo Upload Queue
// ============================================================================

/**
 * Convert a File/Blob to base64 string for IndexedDB storage
 */
export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Queue a photo for upload when online.
 * Stores the photo as base64 in the pending operations queue.
 */
export async function queuePhotoUpload(
  jobId: string,
  fileName: string,
  base64Data: string,
  metadata?: Record<string, unknown>
): Promise<string> {
  return queueOperation('photo_upload', {
    jobId,
    fileName,
    base64Data,
    metadata: metadata || {},
  });
}

/**
 * Get all pending photo uploads
 */
export async function getPendingPhotoUploads(): Promise<PendingOperation[]> {
  const operations = await getPendingOperations();
  return operations.filter(op => op.type === 'photo_upload');
}
